/**
 * OrderFieldChangesConfirmationModal Component
 * Shows summary of field changes (non-status) before updating order
 */

import React from "react";
import {
  Modal,
  Typography,
  Descriptions,
  Divider,
  Alert,
  Space,
  Button,
  Tag,
} from "antd";
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const OrderFieldChangesConfirmationModal = ({
  open,
  order,
  changes, // { field: { oldValue, newValue, label } }
  onConfirm,
  onCancel,
  loading = false,
}) => {
  if (!open || !changes || Object.keys(changes).length === 0) {
    return null;
  }

  const formatValue = (value, field) => {
    if (value === null || value === undefined || value === "") {
      return <Text type="secondary" italic>Not set</Text>;
    }
    
    if (field === "priority") {
      return <Tag>{String(value).charAt(0).toUpperCase() + String(value).slice(1)}</Tag>;
    }
    
    if (field === "estimatedDelivery") {
      // Handle both Date objects and date strings
      try {
        const date = value instanceof Date ? value : new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
        return String(value); // Fallback to string if date parsing fails
      } catch {
        return String(value);
      }
    }
    
    if (typeof value === "object") {
      return value.address || JSON.stringify(value);
    }
    
    return String(value);
  };

  const changeEntries = Object.entries(changes);
  const actions = [
    {
      icon: <ReloadOutlined />,
      label: "Invalidate cache",
      description: "Order cache will be refreshed",
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined style={{ color: "#1890ff" }} />
          <Title level={4} style={{ margin: 0 }}>
            Confirm Order Update
          </Title>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnHidden
    >
      <Alert
        message="Review the changes that will be made"
        description={`${changeEntries.length} field${changeEntries.length !== 1 ? "s" : ""} will be updated. Review the details below before confirming.`}
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 24 }}
      />

      <Divider orientation="left">Field Changes</Divider>

      <Descriptions bordered size="small" column={1}>
        {changeEntries.map(([field, { oldValue, newValue, label }]) => (
          <Descriptions.Item key={field} label={label || field}>
            <Space>
              <Text delete type="secondary">
                {formatValue(oldValue, field)}
              </Text>
              <Text>→</Text>
              <Text strong>
                {formatValue(newValue, field)}
              </Text>
            </Space>
          </Descriptions.Item>
        ))}
      </Descriptions>

      <Divider orientation="left" style={{ marginTop: 24 }}>
        Actions That Will Be Taken
      </Divider>

      <Space direction="vertical" style={{ width: "100%" }}>
        {actions.map((action, index) => (
          <div key={index}>
            <Space>
              {action.icon}
              <Text strong>{action.label}</Text>
            </Space>
            <div style={{ marginTop: 4, marginLeft: 24 }}>
              <Text type="secondary" small>
                {action.description}
              </Text>
            </div>
          </div>
        ))}
      </Space>

      <Divider />

      <Alert
        message="Summary"
        description={
          <Space direction="vertical" size="small">
            <Text>
              • {changeEntries.length} field{changeEntries.length !== 1 ? "s" : ""} will be updated
            </Text>
            <Text>• Cache will be invalidated</Text>
          </Space>
        }
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
        style={{ marginBottom: 24 }}
      />

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={onConfirm}
          loading={loading}
          icon={<CheckCircleOutlined />}
        >
          Confirm & Update
        </Button>
      </div>
    </Modal>
  );
};

export default OrderFieldChangesConfirmationModal;

